const links = [...document.querySelectorAll('[data-coffee-filter-facet]')].filter(
  (link) => link instanceof HTMLAnchorElement,
);
const loader = document.querySelector('[data-coffee-filter-links]');

const canonicalParams = Object.freeze({
  origin: ['filter.p.m.coffee.origin_keys', 'filter.p.m.coffee.origin_key'],
  process: ['filter.p.m.coffee.process_key'],
  variety: ['filter.p.m.coffee.variety_keys'],
});

const legacyParams = Object.freeze({
  origin: 'filter.p.m.custom.country_of_origin',
  process: 'filter.p.m.custom.coffee_process',
  variety: 'filter.p.m.coffee.variety',
});

const knownFacetParams = Object.freeze(
  Object.values(canonicalParams).flat().concat(Object.values(legacyParams)),
);

const normalize = (value) => String(value || '').trim().toLowerCase();
const exactValue = (filter, candidate) =>
  filter?.values?.find((value) => String(value.value || '').trim() === candidate);

const resolveCandidate = (registry, { facet, canonical, legacy }) => {
  const filters = Array.isArray(registry?.filters) ? registry.filters : [];

  if (canonical) {
    for (const paramName of canonicalParams[facet] || []) {
      const filter = filters.find((candidate) => candidate.paramName === paramName);
      const value = exactValue(filter, canonical);
      if (value) return { paramName, value: value.value };
    }
  }

  if (legacy) {
    const legacyParam = legacyParams[facet];
    const legacyFilter = filters.find((candidate) => candidate.paramName === legacyParam);
    const normalizedLegacy = normalize(legacy);
    const values = legacyFilter?.values?.filter((candidate) =>
      normalize(candidate.value) === normalizedLegacy || normalize(candidate.label) === normalizedLegacy,
    );
    if (values?.length) {
      return { paramName: legacyParam, values: [...new Set(values.map((value) => value.value))] };
    }
  }

  return null;
};

const candidateFromLink = (link, prefix = '') => ({
  facet: link.dataset[`coffeeFilter${prefix}Facet`],
  canonical: String(link.dataset[`coffeeFilter${prefix}Canonical`] || '').trim(),
  legacy: String(link.dataset[`coffeeFilter${prefix}Legacy`] || '').trim(),
  label: String(link.dataset[`coffeeFilter${prefix}Label`] || '').trim(),
});

const hydrateLink = (link, registry) => {
  const primary = candidateFromLink(link);
  let candidate = primary;
  let resolved = resolveCandidate(registry, primary);

  if (!resolved && link.dataset.coffeeFilterFallbackFacet) {
    candidate = candidateFromLink(link, 'Fallback');
    resolved = resolveCandidate(registry, candidate);
  }

  if (!resolved) return;

  const url = new URL(link.href, window.location.href);
  knownFacetParams.forEach((paramName) => url.searchParams.delete(paramName));
  const values = resolved.values || [resolved.value];
  values.forEach((value) => url.searchParams.append(resolved.paramName, value));
  link.href = url.toString();
  const label = link.querySelector('[data-coffee-filter-link-label]');
  if (label instanceof HTMLElement && candidate.label) label.textContent = candidate.label;
};

let requestPromise = null;
let attempts = 0;

const loadRegistry = () => {
  if (requestPromise || attempts >= 2 || !(loader instanceof HTMLScriptElement)) return requestPromise;

  attempts += 1;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 5000);
  links.forEach((link) => link.setAttribute('aria-busy', 'true'));

  requestPromise = fetch(loader.dataset.registryUrl, {
    cache: 'no-store',
    headers: { Accept: 'text/html' },
    signal: controller.signal,
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Coffee filter registry failed with ${response.status}`);
      return response.json();
    })
    .then((registry) => links.forEach((link) => hydrateLink(link, registry)))
    .catch(() => {
      requestPromise = null;
    })
    .finally(() => {
      window.clearTimeout(timeout);
      links.forEach((link) => link.removeAttribute('aria-busy'));
    });

  return requestPromise;
};

if (links.length) {
  loadRegistry();
  document.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('[data-product-attribute-trigger]')) {
      loadRegistry();
    }
  });
}
